import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Button } from '../../../../components/ui/Button.js';
import { StatusBadge } from '../../../../components/ui/StatusBadge.js';
import { Skeleton } from '../../../../components/ui/Skeleton.js';
import {
  useSuretyRequestDetail,
  useApproveNominee,
  useRejectNominee,
} from '../../../../features/surety/hooks.js';
import { useCustomer } from '../../../../features/customers/hooks.js';
import { useScheme } from '../../../../features/schemes/network/schemes.api.js';
import { getSession } from '../../../../lib/session.js';
import { usePageTitle } from '../../../../lib/hooks.js';
import {
  ChevronLeft,
  FileText,
  Plus,
  Check,
  X,
  ShieldAlert,
  Users,
  AlertTriangle,
} from 'lucide-react';
import type { SuretyNominee } from '../../../../features/surety/types.js';
import { DataView } from '../../../../components/ui/DataView.js';
import { SuretyDocumentsList } from '../SuretyDocumentsList';
import { DetailPage } from '@repo/web-detail';
import { Forbidden } from '../../../../components/error/Forbidden.js';

export const Route = createFileRoute('/_auth/surety/$id/')({
  component: SuretyRequestDetailPage,
});

// Nominee Card - fetches customer details cross-module
function NomineeCard({
  nominee,
  requestId,
  canUpdate,
  onViewDocuments,
}: {
  nominee: SuretyNominee;
  requestId: string;
  canUpdate: boolean;
  onViewDocuments: (nominee: SuretyNominee) => void;
}) {
  const targetCustomerId = nominee.nomineeMembershipId || nominee.nomineeCustomerId || '';
  const { data: customer, isLoading } = useCustomer(targetCustomerId);
  
  const approveNominee = useApproveNominee();
  const rejectNominee = useRejectNominee();
  const [rejecting, setRejecting] = React.useState(false);
  const [reason, setRejectionReason] = React.useState('');
  const [rejectError, setRejectError] = React.useState('');

  const handleApprove = async () => {
    try {
      await approveNominee.mutateAsync({ requestId, nomineeId: nominee.id });
    } catch (err) {
      // toast is shown by hook; log for debugging
      console.error('Failed to approve nominee:', err);
    }
  };

  const handleReject = async () => {
    if (!reason.trim()) {
      setRejectError('Rejection reason is required.');
      return;
    }
    try {
      setRejectError('');
      await rejectNominee.mutateAsync({
        requestId,
        nomineeId: nominee.id,
        rejectionReason: reason,
      });
      setRejecting(false);
      setRejectionReason('');
    } catch (err) {
      // toast is handled by hook; log for debugging
      console.error('Failed to reject nominee:', err);
    }
  };

  const getNomineeStatusVariant = (status: string): 'warning' | 'info' | 'success' | 'danger' => {
    if (status === 'APPROVED') return 'success';
    if (status === 'REJECTED') return 'danger';
    if (status === 'DOCUMENTS_SUBMITTED') return 'info';
    return 'warning';
  };

  const getNomineeStatusLabel = (status: string) => {
    if (status === 'APPROVED') return 'Approved';
    if (status === 'REJECTED') return 'Rejected';
    if (status === 'DOCUMENTS_SUBMITTED') return 'Docs Submitted';
    return 'Nominated';
  };

  if (isLoading) {
    return (
      <div className="bg-surface-1 border border-border rounded-xl p-5 shadow-sm flex flex-col gap-4">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3 w-2/3" />
        <Skeleton className="h-8 w-full mt-2" />
      </div>
    );
  }

  return (
    <div className="bg-surface-1 border border-border rounded-xl p-5 shadow-sm flex flex-col justify-between gap-4">
      <div>
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-text-primary text-base">
            {customer?.name || 'Nominee Profile'}
          </h3>
          <StatusBadge
            status={getNomineeStatusVariant(nominee.status)}
            label={getNomineeStatusLabel(nominee.status)}
          />
        </div>

        <div className="mt-3 space-y-1.5 text-xs text-text-secondary">
          <p>
            <span className="text-text-muted">Customer No:</span> {customer?.customerCode || '—'}
          </p>
          <p>
            <span className="text-text-muted">Phone:</span> {customer?.mobile || '—'}
          </p>
          <p>
            <span className="text-text-muted">KYC Verified:</span>{' '}
            {nominee.kycVerified ? (
              <span className="text-success-500 font-medium">Yes</span>
            ) : (
              <span className="text-warning-500 font-medium">No</span>
            )}
          </p>
          {nominee.status === 'REJECTED' && nominee.rejectionReason && (
            <div className="mt-2 p-2 bg-danger-bg/40 text-danger-text rounded-[6px] border border-danger-bg flex items-start gap-1.5">
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <p className="text-caption leading-relaxed">
                <span className="font-bold">Reason:</span> {nominee.rejectionReason}
              </p>
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-2 mt-2">
        <Button variant="secondary" size="sm" onClick={() => onViewDocuments(nominee)}>
          <FileText className="w-3.5 h-3.5" />
          Manage Documents ({nominee.documents?.length || 0})
        </Button>

        {canUpdate && nominee.status !== 'APPROVED' && nominee.status !== 'REJECTED' && !rejecting && (
          <div className="flex gap-2">
            <Button
              variant="primary"
              size="sm"
              className="flex-1"
              onClick={() => void handleApprove()}
              loading={approveNominee.isPending}
            >
              <Check className="w-3.5 h-3.5" /> Approve
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="flex-1"
              onClick={() => setRejecting(true)}
            >
              <X className="w-3.5 h-3.5" /> Reject
            </Button>
          </div>
        )}

        {rejecting && (
          <div className="border border-border rounded-lg p-3 bg-surface-2 flex flex-col gap-2">
            <p className="text-xs font-bold text-text-primary">Provide Rejection Reason</p>
            <textarea
              value={reason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="e.g. Document mismatch, signature mismatch..."
              className="w-full text-xs p-2 bg-surface-3 border border-border rounded focus:outline-none min-h-[50px] resize-y"
            />
            {rejectError && <p className="text-caption text-danger-text">{rejectError}</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setRejecting(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => void handleReject()}
                loading={rejectNominee.isPending}
              >
                Confirm Reject
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function SuretyRequestDetailPage() {
  usePageTitle('Surety Request Validation');
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const session = getSession();
  const canRead =
    session?.user?.type === 'staff' ||
    session?.user?.type === 'company_admin' ||
    session?.user?.type === 'group_admin' ||
    (session?.permissions?.includes('chitfunds.surety.read') ?? true);
  const canUpdate =
    session?.user?.type === 'staff' ||
    session?.user?.type === 'company_admin' ||
    session?.user?.type === 'group_admin' ||
    (session?.permissions?.includes('chitfunds.surety.update') ?? true);

  if (!canRead) {
    return <Forbidden />;
  }

  const suretyQuery = useSuretyRequestDetail(id);
  const { data: scheme } = useScheme(suretyQuery.data?.schemeId || '');

  // Document modal state
  const [activeNominee, setActiveNominee] = React.useState<SuretyNominee | null>(null);

  React.useEffect(() => {
    if (activeNominee && suretyQuery.data) {
      const refreshed = suretyQuery.data.nominees.find((n) => n.id === activeNominee.id);
      if (refreshed) {
        setActiveNominee(refreshed);
      }
    }
  }, [suretyQuery.data, activeNominee]);

  const getRequestStatusVariant = (status: string): 'warning' | 'info' | 'success' | 'danger' => {
    if (status === 'BOTH_APPROVED') return 'success';
    if (status === 'REJECTED') return 'danger';
    if (status === 'PARTIAL') return 'info';
    return 'warning';
  };

  const getRequestStatusLabel = (status: string) => {
    if (status === 'BOTH_APPROVED') return 'Both Approved';
    if (status === 'REJECTED') return 'Rejected';
    if (status === 'PARTIAL') return 'Partial Approval';
    return 'Pending';
  };

  const mainPane = (
    <DataView query={suretyQuery}>
      {(request) => (
        <div className="flex flex-col gap-6">
          {/* Overview Card */}
          <div className="bg-surface-1 border border-border rounded-xl p-5 shadow-sm">
            <h2 className="text-sm font-bold text-text-secondary uppercase tracking-wider mb-4 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4 text-accent-500" />
              Request Details
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 text-sm">
              <div>
                <p className="text-text-muted text-xs">Winner Subscriber ID</p>
                <p className="font-mono text-text-primary mt-1 select-all">{request.winnerSubscriberId}</p>
              </div>
              <div>
                <p className="text-text-muted text-xs">Scheme Name</p>
                <p className="font-medium text-text-primary mt-1">
                  {scheme?.displayName || scheme?.schemeNumber || 'Loading...'}
                </p>
              </div>
              <div>
                <p className="text-text-muted text-xs">Payout ID</p>
                <p className="font-mono text-text-primary mt-1 select-all">{request.prizePayoutId}</p>
              </div>
              <div>
                <p className="text-text-muted text-xs">Surety Deadline</p>
                <p className="font-medium text-text-primary mt-1">
                  {new Date(request?.deadline || '').toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Nominees Grid */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-text-secondary uppercase tracking-wider flex items-center gap-2">
                <Users className="w-4 h-4 text-accent-500" />
                Nominee Guarantor ({request.nominees?.length || 0})
              </h2>
              {canUpdate && request.status !== 'BOTH_APPROVED' && request.status !== 'REJECTED' && (
                <Button variant="primary" size="sm" onClick={() => navigate({ to: `/surety/$id/nominees/new`, params: { id } })}>
                  <Plus className="w-4 h-4" /> Add Nominee
                </Button>
              )}
            </div>

            {(!request.nominees || request.nominees.length === 0) ? (
              <div className="bg-surface-1 border border-border rounded-xl p-8 text-center flex flex-col items-center justify-center gap-2">
                <p className="text-sm font-semibold text-text-primary">No nominee profiles added yet</p>
                <p className="text-xs text-text-muted">
                  Add nominee customer details to proceed with verification.
                </p>
                {canUpdate && (
                  <Button variant="primary" size="sm" className="mt-2" onClick={() => navigate({ to: `/surety/$id/nominees/new`, params: { id } })}>
                    <Plus className="w-4 h-4" /> Add First Nominee
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {request.nominees.map((nominee) => (
                  <NomineeCard
                    key={nominee.id}
                    nominee={nominee}
                    requestId={id}
                    canUpdate={canUpdate}
                    onViewDocuments={(n) => setActiveNominee(n)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </DataView>
  );

  const asidePane = (
    <DataView query={suretyQuery}>
      {(request) => (
        <div className="bg-surface-1 border border-border rounded-xl p-5 shadow-sm flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-xs font-bold text-text-secondary uppercase tracking-wide">
              Overall Status
            </label>
            <div className="mt-1">
              <StatusBadge
                status={getRequestStatusVariant(request.status)}
                label={getRequestStatusLabel(request.status)}
                className="px-3 py-1 text-sm"
              />
            </div>
          </div>

          <div className="border-t border-border pt-4 text-xs text-text-secondary space-y-2">
            <p>
              <span className="text-text-muted">Created:</span>{' '}
              {new Date(request?.createdAt || '').toLocaleString()}
            </p>
            <p>
              <span className="text-text-muted">Last Updated:</span>{' '}
              {new Date(request?.updatedAt || '').toLocaleString()}
            </p>
          </div>
        </div>
      )}
    </DataView>
  );

  return (
    <div>
      <DetailPage
        config={{
          resource: 'surety',
          fetchById: async () => suretyQuery.data!,
          tabs: [],
        }}
        detail={{
          data: suretyQuery.data,
          isLoading: suretyQuery.isLoading,
          isError: suretyQuery.isError,
          error: suretyQuery.error as Error | null,
          refetch: suretyQuery.refetch,
          activeTab: '',
          setActiveTab: () => {},
          isEditing: false,
          setIsEditing: () => {},
          startEditing: () => {},
          cancelEditing: () => {},
          pageTitle: 'Surety Request Validation',
        }}
        title="Surety Request Validation"
        subtitle={`Request #${id.slice(0, 8)}…`}
        backButton={<Link to="/surety" className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors w-fit"><ChevronLeft className="h-4 w-4" />Back</Link>}
        mainPane={mainPane}
        asidePane={asidePane}
      />

      {/* Documents Modal */}
      <SuretyDocumentsList
        activeNominee={activeNominee}
        onClose={() => setActiveNominee(null)}
        requestId={id}
        canUpdate={canUpdate}
        onActiveNomineeChange={setActiveNominee}
      />
    </div>
  );
}
